// AXIS ZERO - Main Application
// Mobile-first endless runner game with all features

import React, { useState, useEffect, useCallback, useRef } from 'react';
import "@/App.css";
import { Toaster } from 'sonner';
import { useGameLoop } from './hooks/useGameLoop';
import { GameCanvas, HUD, GameOverOverlay, StartScreen, NicknameModal, OrientationHint } from './components/game';
import { GAME_STATES, leaderboardAPI } from './game';

function App() {
  const {
    canvasRef,
    containerRef,
    gameState,
    gameMode,
    score,
    streak,
    isHighStreak,
    highScore,
    isNewHighScore,
    gameOverData,
    isMuted,
    startGame,
    handleTap,
    restartGame,
    toggleSound,
    startPracticeMode,
    initAudio,
    exitPractice,
    restartPractice,
  } = useGameLoop();

  // Nickname modal state
  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const [pendingScore, setPendingScore] = useState(null);
  
  // Leaderboard result state
  const [leaderboardResult, setLeaderboardResult] = useState(null);
  
  // Personal best state (initialized from localStorage)
  const [personalBest, setPersonalBest] = useState(() => leaderboardAPI.getPersonalBest());
  const [personalBestRank, setPersonalBestRank] = useState(() => leaderboardAPI.getStoredPersonalBestRank());
  
  // Ref to track if we've submitted for current game
  const submittedRef = useRef(false);

  // Handle game over - check for new personal best
  useEffect(() => {
    if (gameState === GAME_STATES.GAME_OVER && !submittedRef.current) {
      submittedRef.current = true;
      
      const isPractice = gameOverData?.isPracticeMode;
      
      if (!isPractice) {
        // Convert score to centiseconds for backend
        const scoreCs = Math.floor(score * 100);
        
        // Check if it's a new personal best
        const currentBest = leaderboardAPI.personalBestScore;
        const isNewPB = currentBest === null || scoreCs > currentBest;
        
        if (isNewPB) {
          // Show nickname modal for new high score
          setPendingScore(scoreCs);
          setShowNicknameModal(true);
        } else {
          // Auto-submit (backend will return current PB)
          submitScore(scoreCs, null);
        }
      }
    } else if (gameState !== GAME_STATES.GAME_OVER) {
      // Reset for next game
      submittedRef.current = false;
    }
  }, [gameState, score, gameOverData]);

  // Submit score to backend
  const submitScore = useCallback(async (scoreCs, nickname) => {
    try {
      const result = await leaderboardAPI.submitScore(scoreCs, nickname);
      
      if (result) {
        setLeaderboardResult(result);
        
        // Update local personal best display
        if (result.personalBestScore !== undefined) {
          setPersonalBest(prev => ({
            ...prev,
            score: result.personalBestScore,
            nickname: result.yourEntry?.nickname || prev.nickname,
          }));
        }
        if (result.personalBestRank !== undefined) {
          setPersonalBestRank(result.personalBestRank);
        }
      }
    } catch (e) {
      console.error('[App] Submit score error:', e);
    }
  }, []);

  // Handle nickname save from modal
  const handleNicknameSave = useCallback((nickname) => {
    setShowNicknameModal(false);
    
    if (pendingScore !== null) {
      submitScore(pendingScore, nickname);
      setPendingScore(null);
    }
  }, [pendingScore, submitScore]);

  // Get display high score - use local storage value, never show 0 incorrectly
  const displayHighScore = personalBest.score !== null 
    ? personalBest.score / 100  // Convert from centiseconds
    : null;

  return (
    <div 
      data-testid="app-container"
      className="App relative w-screen overflow-hidden"
      style={{ 
        backgroundColor: '#000000',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        height: '100dvh',
        minHeight: '100%',
      }}
    >
      {/* Game Canvas */}
      <GameCanvas 
        canvasRef={canvasRef}
        containerRef={containerRef}
        onTap={handleTap}
      />

      {/* HUD */}
      <HUD 
        gameState={gameState}
        score={score}
        highScore={displayHighScore}
        streak={streak}
        isHighStreak={isHighStreak}
        isMuted={isMuted}
        onToggleSound={toggleSound}
      />

      {/* Start Screen */}
      <StartScreen 
        gameState={gameState}
        highScore={displayHighScore}
        personalBestRank={personalBestRank}
        onStart={startGame}
        isMuted={isMuted}
        onToggleSound={toggleSound}
      />

      {/* Game Over Overlay */}
      <GameOverOverlay 
        gameState={gameState}
        score={score}
        highScore={displayHighScore}
        isNewHighScore={isNewHighScore}
        onRestart={restartGame}
        onBack={exitPractice}
        gameOverData={gameOverData}
        leaderboardResult={leaderboardResult}
      />

      {/* Nickname Modal (for new high scores) */}
      <NicknameModal
        isOpen={showNicknameModal}
        onClose={() => {
          setShowNicknameModal(false);
          if (pendingScore !== null) {
            submitScore(pendingScore, null);
            setPendingScore(null);
          }
        }}
        onSave={handleNicknameSave}
        score={pendingScore ? pendingScore / 100 : 0}
        defaultNickname={leaderboardAPI.getNickname()}
      />

      {/* Orientation Hint (portrait mode) */}
      <OrientationHint />

      {/* Toast notifications */}
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#121212',
            color: '#00F0FF',
            border: '1px solid #00F0FF',
            fontFamily: '"Rajdhani", sans-serif',
          },
        }}
      />
    </div>
  );
}

export default App;
